import classNames from "classnames";
import { ImportedAccountMappingCounts } from "@common/helpers/importedAccountMappingResolution";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { EditChartOfAccountsButton } from "@components/accounting/chartOfAccounts/EditChartOfAccountsButton";
import { EditorIssueBar } from "@components/settings/ChartOfAccountsTemplates/editor/EditorIssueBar";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { HStack } from "@design-system/HStack/HStack";
import { Separator } from "@design-system/Separator/Separator";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { CoaMappingCounts } from "./CoaMappingCounts";
import styles from "./CoaMappingDrawer.module.scss";

interface Props {
    isLoaded: boolean;
    isMappingPending: boolean;
    isCreateAccountModalOpen: boolean;
    // Preview's mod+enter must not fire under the editor drawer.
    isChartEditorOpen: boolean;
    importedCount: number;
    counts: ImportedAccountMappingCounts;
    currentUnmappedIndex: number;
    currentUnmappedName: string | undefined;
    onNavigateUnmapped: (index: number) => void;
    onClose: () => void;
    onEditChart: () => void;
    onPreview: () => void;
}

const UNMAPPED_TOOLTIP = "All imported accounts must be mapped first";

export const CoaMappingCommandIsland = ({
    isLoaded,
    isMappingPending,
    isCreateAccountModalOpen,
    isChartEditorOpen,
    importedCount,
    counts,
    currentUnmappedIndex,
    currentUnmappedName,
    onNavigateUnmapped,
    onClose,
    onEditChart,
    onPreview,
}: Props) => {
    const canEditCoA = useCanPerformWorkspaceAction(WorkspaceAction.COA_EDIT);
    const hasUnmapped = counts.notMapped > 0;
    return (
        <CommandIsland sticky>
            <HStack gap={8} align="center" className={styles.islandLeading}>
                <div className={styles.islandLeadingItem}>
                    <CoaMappingCounts
                        importedCount={importedCount}
                        counts={counts}
                    />
                </div>
                {hasUnmapped && (
                    <>
                        <Separator
                            orientation="vertical"
                            className={styles.islandSeparator}
                        />
                        <div className={styles.islandLeadingItem}>
                            <EditorIssueBar
                                label="Unmapped"
                                issueCount={counts.notMapped}
                                currentIssueIndex={currentUnmappedIndex}
                                currentIssueMessage={currentUnmappedName}
                                onNavigateIssue={onNavigateUnmapped}
                            />
                        </div>
                    </>
                )}
            </HStack>
            <HStack
                gap={8}
                align="center"
                className={classNames("ml-auto", styles.islandActions)}
            >
                <Button variant="secondary" size="md" onClick={onClose}>
                    Close
                </Button>
                <EditChartOfAccountsButton
                    variant="secondary"
                    canEditCoA={canEditCoA}
                    isDisabled={!isLoaded}
                    onOpen={onEditChart}
                />
                <ButtonWithLoader
                    variant="primary"
                    size="md"
                    loading={false}
                    disabled={
                        !isLoaded ||
                        isMappingPending ||
                        hasUnmapped ||
                        isCreateAccountModalOpen ||
                        isChartEditorOpen
                    }
                    tooltip={hasUnmapped ? UNMAPPED_TOOLTIP : undefined}
                    hotKey="mod+enter"
                    hotKeyOptions={{ description: "Preview" }}
                    hotKeyDisplayPosition="inline"
                    onClick={onPreview}
                >
                    Preview
                </ButtonWithLoader>
            </HStack>
        </CommandIsland>
    );
};
